import { makeAutoObservable, runInAction } from 'mobx'
import { api } from '@/shared/api/endpoints'
import { onSessionExpired, setAccessToken } from '@/shared/api/http'
import { needsTwoFactor, type UserResponse } from '@/shared/api/types'
import type { TelegramUser } from '@/shared/lib/telegram'
import { explain } from '@/shared/lib/errors'
import type { UiStore } from '@/stores/UiStore'

/**
 * Где живёт токен.
 *
 * `sessionStorage`, а не `localStorage`. Разница практическая:
 * localStorage переживает закрытие браузера, и на общем компьютере
 * следующий человек открывает вкладку и оказывается в чужом кабинете
 * с чужими деньгами. sessionStorage умирает вместе с вкладкой.
 *
 * Совсем не хранить (только в памяти) было бы ещё строже, но тогда
 * любое обновление страницы выкидывает на вход — а токен и без того
 * живёт пятнадцать минут.
 */
const TOKEN_KEY = 'cck-token'

export type LoginStep =
  | { kind: 'idle' }
  | { kind: 'two-factor'; token: string }

export class AuthStore {
  user: UserResponse | null = null
  /**
   * Токен есть, но кто мы — ещё не знаем.
   *
   * Начальное значение выставляется в конструкторе, а НЕ при вызове
   * restore(): restore() запускается из эффекта, то есть уже после
   * первого рендера. Пока это поле было false, первый рендер видел
   * «токен есть, пользователя нет» и уводил на вход, а обратно
   * возвращал уже на /app — человек перезагружал страницу истории
   * и терял её, а заодно и позицию в списке.
   */
  restoring = false
  busy = false
  error: string | null = null
  step: LoginStep = { kind: 'idle' }
  /** Сессия оборвалась сама — показать причину на экране входа. */
  expired = false

  private token: string | null = null
  private readonly ui: UiStore

  constructor(ui: UiStore) {
    this.ui = ui
    makeAutoObservable<this, 'ui' | 'token' | 'restorePromise'>(
      this, { ui: false, token: false, restorePromise: false },
    )

    this.token = readToken()
    this.restoring = this.token !== null
    setAccessToken(this.token)
    onSessionExpired(() => this.handleExpired())
  }

  get authenticated(): boolean {
    return this.user !== null
  }

  get isAdmin(): boolean {
    return this.user?.role === 'admin'
  }

  /**
   * Первый шаг после загрузки приложения: есть ли живая сессия.
   *
   * Вызов повторяется — React в строгом режиме прогоняет эффекты
   * дважды. Второй заход не должен слать второй /me и уж точно не
   * должен заново поднимать restoring, когда первый уже опустил его:
   * кабинет мигнёт заглушкой поверх готовых данных.
   */
  private restorePromise: Promise<void> | null = null

  async restore(): Promise<void> {
    if (this.token === null) {
      this.restoring = false
      return
    }
    if (this.restorePromise !== null) return this.restorePromise
    this.restorePromise = this.doRestore()
    return this.restorePromise
  }

  private async doRestore(): Promise<void> {
    this.restoring = true
    try {
      const user = await api.account.me()
      runInAction(() => { this.user = user })
    } catch {
      // Токен протух или отозван — молча возвращаемся к входу.
      // Ошибку не показываем: человек ничего не делал.
      this.clear()
    } finally {
      runInAction(() => { this.restoring = false })
    }
  }

  async login(email: string, password: string): Promise<void> {
    this.busy = true
    this.error = null
    this.expired = false
    try {
      const result = await api.auth.login(email, password)
      if (needsTwoFactor(result)) {
        runInAction(() => { this.step = { kind: 'two-factor', token: result['2fa_token'] } })
        return
      }
      await this.accept(result.access_token)
    } catch (cause) {
      runInAction(() => { this.error = explain(cause) })
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  /**
   * Вход через Telegram. Тот же путь, что и пароль: бекенд отвечает
   * либо токеном, либо требованием второго фактора — Telegram заменяет
   * пароль, а не 2FA.
   */
  async loginTelegram(user: TelegramUser): Promise<void> {
    this.busy = true
    this.error = null
    this.expired = false
    try {
      const result = await api.auth.telegram(user)
      if (needsTwoFactor(result)) {
        runInAction(() => { this.step = { kind: 'two-factor', token: result['2fa_token'] } })
        return
      }
      await this.accept(result.access_token)
    } catch (cause) {
      runInAction(() => { this.error = explain(cause) })
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  /** Второй фактор: код из приложения либо резервный вида ABCD-1234. */
  async submitTwoFactor(code: string): Promise<void> {
    if (this.step.kind !== 'two-factor') return
    this.busy = true
    this.error = null
    try {
      const result = await api.auth.twoFactor(this.step.token, code.trim().toUpperCase())
      await this.accept(result.access_token)
    } catch (cause) {
      runInAction(() => { this.error = explain(cause) })
    } finally {
      runInAction(() => { this.busy = false })
    }
  }

  backToPassword(): void {
    this.step = { kind: 'idle' }
    this.error = null
  }

  /**
   * Выход. Ручки на бекенде нет — токен просто забывается здесь.
   *
   * Это значит, что выданный токен остаётся действительным до конца
   * своих пятнадцати минут: украденный до выхода он не отзывается.
   * Настоящий выход требует отзыва на сервере, и это в списке дыр.
   */
  logout(): void {
    this.clear()
    this.ui.success('Вы вышли из аккаунта')
  }

  /**
   * Забыть сессию молча.
   *
   * Нужно там, где токены погасил сервер, а не человек: после смены
   * почты сессия мертва, но «вы вышли из аккаунта» это ложь — он
   * никуда не выходил, у него сменился адрес. Своё сообщение
   * показывает то место, которое знает причину.
   */
  forget(): void {
    this.clear()
  }

  private async accept(token: string): Promise<void> {
    writeToken(token)
    setAccessToken(token)
    runInAction(() => {
      this.token = token
      this.step = { kind: 'idle' }
      this.expired = false
    })
    const user = await api.account.me()
    runInAction(() => { this.user = user })
  }

  private handleExpired(): void {
    if (this.user === null) return
    this.clear()
    this.expired = true
  }

  private clear(): void {
    this.token = null
    this.user = null
    // Сессии больше нет — следующий вход должен уметь восстановиться
    // заново, а не наткнуться на обещание от прошлой сессии.
    this.restorePromise = null
    this.step = { kind: 'idle' }
    setAccessToken(null)
    clearToken()
  }

  /** Профиль изменился (сменили пароль, включили 2FA) — перечитать. */
  async refreshUser(): Promise<void> {
    if (this.token === null) return
    try {
      const user = await api.account.me()
      runInAction(() => { this.user = user })
    } catch (cause) {
      this.ui.failure(explain(cause))
    }
  }
}

function readToken(): string | null {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

function writeToken(token: string): void {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token)
  } catch {
    // Хранилище запрещено — сессия проживёт до перезагрузки.
  }
}

function clearToken(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY)
  } catch {
    // см. writeToken
  }
}
